/**
 * Enregistrement du service worker et abonnement aux notifications.
 *
 * Contrainte iOS (brief §3.7) : sur iPhone, `Notification.requestPermission()`
 * n'est disponible QUE si la PWA a été ajoutée à l'écran d'accueil. Dans
 * Safari onglet, l'API existe parfois mais l'abonnement échoue. D'où
 * `needsInstall()`, qui permet d'afficher l'onboarding d'installation plutôt
 * qu'un bouton qui ne marche pas.
 */

import { api } from "./api.js";

/** Marqueur de rechargement, pour ne purger qu'une fois par onglet. */
const PURGE_KEY = "rm-sw-purged";

/**
 * Enregistre le service worker — sauf en développement, où on désinstalle au
 * contraire celui qu'une session précédente aurait laissé.
 *
 * **Pourquoi il n'a rien à faire en dev.** Sa stratégie cache-first est juste
 * en production : Vite empreinte les noms de fichiers, une URL d'asset ne
 * désigne donc jamais deux contenus. En dev c'est l'inverse — l'URL d'un
 * module source est stable (`/src/main.tsx`) alors que celle d'une dépendance
 * pré-bundlée porte un jeton qui change à chaque ré-optimisation
 * (`/node_modules/.vite/deps/react.js?v=…`). Le SW ressert alors un module
 * qui importe une URL disparue : le graphe d'imports casse, React ne monte
 * jamais, et on obtient un écran noir avec pour seule trace un `ERR_FAILED`
 * sans nom de fichier. Symptôme coûteux à diagnostiquer, cause sans intérêt.
 *
 * La désinstallation est donc auto-réparatrice : il suffit de charger l'app en
 * dev une fois pour se débarrasser d'un SW hérité.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;

  if (import.meta.env.DEV) {
    // Vrai si la page courante est déjà servie par le SW : ses modules
    // viennent peut-être du cache qu'on s'apprête à vider.
    const controlled = Boolean(navigator.serviceWorker.controller);

    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((r) => r.unregister()));
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }

    /**
     * Un rechargement, un seul. Le marqueur de session est une ceinture : au
     * tour suivant `controller` est nul, donc la condition est déjà fausse —
     * mais si la désinstallation échouait, un écran noir vaut mieux qu'une
     * boucle de rechargement infinie.
     */
    if (controlled && !sessionStorage.getItem(PURGE_KEY)) {
      sessionStorage.setItem(PURGE_KEY, "1");
      location.reload();
    }
    return null;
  }

  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch (err) {
    console.warn("Service worker non enregistré", err);
    return null;
  }
}

/** iOS hors écran d'accueil : le push est impossible, il faut installer d'abord. */
export function needsInstall(): boolean {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (!isIOS) return false;
  const standalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as { standalone?: boolean }).standalone === true;
  return !standalone;
}

export function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function permission(): NotificationPermission | "unsupported" {
  return pushSupported() ? Notification.permission : "unsupported";
}

/**
 * Demande la permission et abonne le device.
 * Retourne un message d'erreur lisible, ou `null` si tout s'est bien passé.
 */
export async function subscribe(): Promise<string | null> {
  if (!pushSupported()) return "Ce navigateur ne supporte pas les notifications.";
  if (needsInstall()) {
    return "Sur iPhone, ajoute d'abord l'app à l'écran d'accueil (Partager → Sur l'écran d'accueil).";
  }

  const result = await Notification.requestPermission();
  if (result !== "granted") return "Permission refusée.";

  let key: string;
  try {
    const response = await api.get<{ key: string }>("/push/vapid-key");
    key = response.key;
  } catch {
    return "Push non configuré côté serveur (VAPID absent).";
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key),
  });

  await api.post("/push/subscribe", subscription.toJSON());
  return null;
}

export async function unsubscribe(): Promise<void> {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  await api.post("/push/unsubscribe", { endpoint: subscription.endpoint });
  await subscription.unsubscribe();
}

/**
 * La clé VAPID transite en base64url ; `PushManager.subscribe` veut un
 * Uint8Array. Conversion obligatoire, et source classique de bugs silencieux.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(normalized);

  // On alloue explicitement un ArrayBuffer : depuis TS 5.7, `Uint8Array` est
  // générique sur son buffer, et `Uint8Array.from` produit un
  // `Uint8Array<ArrayBufferLike>` que `applicationServerKey` refuse.
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) view[i] = raw.charCodeAt(i);
  return view;
}
